import type { GaitResult, Side } from './gait'
import { SIDES, sampleAngle } from './gait'
import type { GroundResult } from './ground'
import type { NodeKey, Pose, Skeleton } from './kinematics'
import { poseAt } from './kinematics'
import type { HumanoidModel } from './model'
import { add, apply, vec, type Vec3 } from './vec'

/**
 * Freigang — wo die Maschine sich selbst im Weg steht.
 *
 * DAS KAPSELMODELL. Jedes Bauteil ist eine Strecke zwischen zwei Punkten plus
 * ein Radius. Das ist grob, und das ist beabsichtigt: bei einer
 * Freigangsprüfung ist ÜBERSCHÄTZUNG das richtige Vorzeichen. Eine Kapsel, die
 * etwas dicker ist als das Bauteil, meldet einen Konflikt zu früh — eine, die
 * zu dünn ist, meldet ihn gar nicht.
 *
 * Die Radien stammen aus den Schalenmassen des Aufbaus. Sie sind damit keine
 * Rechengrösse aus dem Punktesatz, sondern eine Annahme über die Hülle, und sie
 * stehen deshalb hier beisammen und nicht verstreut.
 *
 * ZWEI ABSTÄNDE, UND DER UNTERSCHIED IST DER GANZE WITZ:
 *
 *   GLEICHZEITIG  beide Bauteile in derselben Phase. Das passiert wirklich.
 *   ZEITVERSETZT  jede Phase gegen jede. Das passiert, sobald der Gang von der
 *                 gerechneten Kadenz abweicht — beim Anfahren, beim Stolpern,
 *                 bei einer Störung.
 *
 * Ein Paar, das gleichzeitig 40 mm Luft hat und zeitversetzt −5 mm, ist im
 * Normalbetrieb unauffällig und in jeder Abweichung ein Treffer. Genau diese
 * Paare sucht man.
 */

/* -------------------------------------------------------------------------
   Die Körper
   ------------------------------------------------------------------------- */

/** Wo ein Kapselende hängt und wie weit es vom Knoten weg sitzt. */
export interface CapsuleEnd {
  node: NodeKey
  side?: Side
  offset: Vec3
}

export interface Capsule {
  key: string
  label: string
  a: CapsuleEnd
  b: CapsuleEnd
  /** Radius in Weltmetern. */
  radius: number
  group: 'bein' | 'arm' | 'rumpf'
  side?: Side
}

/**
 * Die Kapselradien, in Konstruktionsmetern.
 *
 * Bewusst grosszügig — siehe oben. Wer sie an eine CAD-Hülle anpasst, ändert
 * hier eine Zahl und bekommt eine schärfere Prüfung; die Rechnung bleibt.
 */
const RADIUS = {
  thigh: 0.058,
  shank: 0.047,
  foot: 0.044,
  upperArm: 0.042,
  forearm: 0.034,
  hand: 0.026,
  pelvis: 0.082,
  waist: 0.058,
  chest: 0.1,
  head: 0.084,
} as const

const LABEL: Record<Side, string> = { R: 'rechts', L: 'links' }

/**
 * Baut die Kollisionskörper aus dem Skelett.
 *
 * Die Rumpfkapseln folgen den Gelenkpunkten wie alles andere: die Taille reicht
 * bis kurz unter das Rumpfgelenk, der Brustkorb bis kurz unter die
 * Schulterachse. Wer den Brustflansch verschiebt, bekommt einen anderen
 * Brustkorb — und eine andere Freigangsprüfung.
 */
export function buildCapsules(skeleton: Skeleton, scale: number): Capsule[] {
  const s = (v: number) => v * scale
  const list: Capsule[] = []

  for (const side of SIDES) {
    const at = (node: NodeKey, x = 0, y = 0, z = 0): CapsuleEnd => ({
      node,
      side,
      offset: vec(x, y, z),
    })

    list.push(
      {
        key: `thigh${side}`,
        label: `Oberschenkel ${LABEL[side]}`,
        a: at('hip'),
        b: at('knee'),
        radius: s(RADIUS.thigh),
        group: 'bein',
        side,
      },
      {
        key: `shank${side}`,
        label: `Schienbein ${LABEL[side]}`,
        a: at('knee'),
        b: at('ankle'),
        radius: s(RADIUS.shank),
        group: 'bein',
        side,
      },
      {
        /* Der Fuss spannt von der Ferse zur Zehenspitze — über das
           Zehengrundgelenk hinweg, denn die Kapsel ist gerade und der Fuss
           knickt dort ab. Die Ferse wird auf halbe Höhe gesetzt, sonst liegt
           die Kapselachse in der Sohle und der Radius reicht in den Boden. */
        key: `foot${side}`,
        label: `Fuss ${LABEL[side]}`,
        a: at('ankle', skeleton.limb[side].heel.x, skeleton.limb[side].heel.y * 0.5, 0),
        b: at('mtp', skeleton.limb[side].toe.x, skeleton.limb[side].toe.y, 0),
        radius: s(RADIUS.foot),
        group: 'bein',
        side,
      },
      {
        key: `upperArm${side}`,
        label: `Oberarm ${LABEL[side]}`,
        a: at('shoulder'),
        b: at('elbow'),
        radius: s(RADIUS.upperArm),
        group: 'arm',
        side,
      },
      {
        key: `forearm${side}`,
        label: `Unterarm ${LABEL[side]}`,
        a: at('elbow'),
        b: at('wrist'),
        radius: s(RADIUS.forearm),
        group: 'arm',
        side,
      },
      {
        key: `hand${side}`,
        label: `Hand ${LABEL[side]}`,
        a: at('wrist', 0, s(-0.02), 0),
        b: at('wrist', s(0.01), s(-0.12), 0),
        radius: s(RADIUS.hand),
        group: 'arm',
        side,
      },
    )
  }

  /*
    Die Endpunkte der Rumpfkapseln liegen um den Radius nach innen versetzt,
    damit die Kapsel AUSSEN genau auf dem Mass endet und nicht darüber
    hinaussteht. Wer das vergisst, bekommt einen Roboter, der um zwei
    Kapselradien breiter ist als sein Bild.
  */
  const pelvisHalf = Math.max(s(0.005), skeleton.hipLateral - s(RADIUS.pelvis))
  list.push({
    key: 'pelvis',
    label: 'Becken',
    a: { node: 'pelvis', offset: vec(0, s(-0.02), -pelvisHalf) },
    b: { node: 'pelvis', offset: vec(0, s(-0.02), pelvisHalf) },
    radius: s(RADIUS.pelvis),
    group: 'rumpf',
  })

  list.push({
    key: 'waist',
    label: 'Taille',
    a: { node: 'pelvis', offset: vec(0, s(0.14), 0) },
    b: { node: 'pelvis', offset: vec(0, Math.max(s(0.16), skeleton.spineRise - s(0.05)), 0) },
    radius: s(RADIUS.waist),
    group: 'rumpf',
  })

  list.push({
    key: 'chest',
    label: 'Brustkorb',
    /* `trunk` ist im Kern der Name des Brustknotens.

       HIER STAND DER VERSATZ AUF DER QUERACHSE (z ∓32 mm): die Kapsel lief
       schräg von rechts unten nach links oben, und der linke Arm traf den
       Brustkorb zehn Grad früher als der rechte. Beim HS-2 fiel das nicht auf,
       weil die Arme zehn Grad abgespreizt hingen; beim Gen5 mit seinen weiter
       aussen liegenden Händen schon. Der Versatz gehört auf die Längsachse:
       unten etwas zurück, oben etwas vor — links wie rechts gleich. */
    a: { node: 'trunk', offset: vec(s(-0.032), s(0.01), 0) },
    b: {
      node: 'trunk',
      offset: vec(s(0.032), Math.max(s(0.03), skeleton.shoulderRise - s(0.02)), 0),
    },
    radius: s(RADIUS.chest),
    group: 'rumpf',
  })

  list.push({
    key: 'head',
    label: 'Kopf',
    a: { node: 'head', offset: vec(0, s(-0.02), 0) },
    b: { node: 'head', offset: vec(0, s(0.02), 0) },
    radius: s(RADIUS.head),
    group: 'rumpf',
  })

  return list
}

/**
 * Paare, die über ein Gelenk verbunden sind.
 *
 * Die berühren einander IMMER — ein Oberschenkel und ein Schienbein teilen sich
 * die Knieachse. Sie zu prüfen hiesse, die Liste mit Nullabständen zu füllen,
 * die alle richtig und alle bedeutungslos sind.
 */
export function neighbourPairs(): Set<string> {
  const skip = new Set<string>()
  const pair = (a: string, b: string) => {
    skip.add(`${a}|${b}`)
    skip.add(`${b}|${a}`)
  }

  for (const side of SIDES) {
    pair(`thigh${side}`, `shank${side}`)
    pair(`shank${side}`, `foot${side}`)
    pair(`thigh${side}`, 'pelvis')
    pair(`thigh${side}`, 'waist')
    pair(`upperArm${side}`, `forearm${side}`)
    pair(`forearm${side}`, `hand${side}`)
    pair(`upperArm${side}`, 'chest')
  }
  pair('pelvis', 'waist')
  pair('waist', 'chest')
  pair('chest', 'head')

  return skip
}

/* -------------------------------------------------------------------------
   Abstand zweier Strecken
   ------------------------------------------------------------------------- */

/**
 * Kürzester Abstand zweier Strecken im Raum.
 *
 * Ohne Zwischenobjekte, mit rohen Zahlen: die Prüfung rechnet bei 120 Phasen
 * und 16 Körpern rund eine Million Aufrufe für den zeitversetzten Fall. Ein
 * Vektorobjekt je Aufruf wäre eine Million Objekte für den Speicherbereiniger.
 *
 * Die dreifache Auflösung von `s` ist kein Tippfehler, sondern das übliche
 * Verfahren: erst grob, dann `t` dazu geklemmt, dann `s` mit dem geklemmten `t`
 * nachgezogen. Ohne den dritten Schritt liegt der Punkt bei fast parallelen
 * Strecken daneben.
 */
export function segmentDistance(
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  cx: number,
  cy: number,
  cz: number,
  dx: number,
  dy: number,
  dz: number,
): number {
  const ux = bx - ax
  const uy = by - ay
  const uz = bz - az
  const vx = dx - cx
  const vy = dy - cy
  const vz = dz - cz
  const wx = ax - cx
  const wy = ay - cy
  const wz = az - cz

  const a = ux * ux + uy * uy + uz * uz
  const b = ux * vx + uy * vy + uz * vz
  const c = vx * vx + vy * vy + vz * vz
  const d = ux * wx + uy * wy + uz * wz
  const e = vx * wx + vy * wy + vz * wz
  const den = a * c - b * b

  let s = den > 1e-12 ? (b * e - c * d) / den : 0
  s = s < 0 ? 0 : s > 1 ? 1 : s
  let t = c > 1e-12 ? (b * s + e) / c : 0
  t = t < 0 ? 0 : t > 1 ? 1 : t
  s = a > 1e-12 ? (b * t - d) / a : 0
  s = s < 0 ? 0 : s > 1 ? 1 : s

  const px = wx + s * ux - t * vx
  const py = wy + s * uy - t * vy
  const pz = wz + s * uz - t * vz
  return Math.sqrt(px * px + py * py + pz * pz)
}

/** Die beiden Endpunkte einer Kapsel in einer Pose. */
export function capsuleEnds(pose: Pose, capsule: Capsule): [Vec3, Vec3] {
  return [endPoint(pose, capsule.a), endPoint(pose, capsule.b)]
}

function endPoint(pose: Pose, end: CapsuleEnd): Vec3 {
  const transform = resolve(pose, end.node, end.side)
  return add(transform.position, apply(transform.rotation, end.offset))
}

function resolve(pose: Pose, key: NodeKey, side: Side = 'R') {
  switch (key) {
    case 'pelvis':
      return pose.pelvis
    case 'trunk':
      return pose.chest
    case 'head':
      return pose.head
    case 'hip':
      return pose.hip[side]
    case 'knee':
      return pose.knee[side]
    case 'ankle':
      return pose.ankle[side]
    case 'mtp':
    case 'ball':
    case 'heel':
    case 'toe':
      return pose.mtp[side]
    case 'shoulder':
      return pose.shoulder[side]
    case 'elbow':
      return pose.elbow[side]
    case 'wrist':
    case 'hands':
      return pose.wrist[side]
    default:
      return pose.chest
  }
}

/* -------------------------------------------------------------------------
   Die Untersuchung
   ------------------------------------------------------------------------- */

export interface ClearancePair {
  a: string
  b: string
  labelA: string
  labelB: string
  /** Kleinster Abstand, wenn beide Körper in derselben Phase stehen, m. */
  synchronous: number
  /** Die Phase, in der das eintritt. */
  synchronousPhase: number
  /** Kleinster Abstand über alle Phasenpaare, m. */
  asynchronous: number
  /** Die beiden Phasen, in denen das eintritt. */
  phaseA: number
  phaseB: number
  /**
   * Gehören die beiden Körper zu verschiedenen Körperseiten?
   *
   * DAS ÄNDERT, WAS DER ZEITVERSETZTE ABSTAND BEDEUTET. Linkes und rechtes Bein
   * sind im Gang fest gekoppelt: sie sind immer eine halbe Periode auseinander,
   * und der gerechnete Gang enthält diesen Versatz bereits. Dass der rechte Fuss
   * zeitversetzt dort steht, wo der linke später steht, ist kein Entwurfsfehler
   * — so geht Gehen.
   *
   * Für ein solches Paar beantwortet der zeitversetzte Abstand die Frage „was
   * passiert, wenn die Beine aus dem Takt geraten", und das ist eine sinnvolle,
   * aber andere Frage als „steht sich der Aufbau selbst im Weg".
   */
  contralateral: boolean
}

export interface ClearanceResult {
  pairs: ClearancePair[]
  /** Der engste gleichzeitige Abstand über alle Paare, m. */
  tightest: number
  /** Wie viele Paare gleichzeitig unter dem geforderten Freigang bleiben. */
  breaches: number
  /** Wie viele nur zeitversetzt darunter bleiben — die stillen Fälle. */
  latent: number
  /** Wie viele davon gegenüberliegende Gliedmassen sind: ein Taktverlust, kein Aufbaufehler. */
  outOfStep: number
  /** Zahl der geprüften Körper. */
  bodies: number
  /** Auflösung der Abtastung. */
  samples: number
}

/** Geforderter Freigang zwischen zwei Bauteilen, m. */
export const CLEARANCE_MIN = 0.01

/** Abtastung: Phasen für den gleichzeitigen Fall. */
const SAMPLES = 120

/**
 * Für den zeitversetzten Fall wird nur jede zweite Phase geprüft.
 *
 * Das sind 60 × 60 statt 120 × 120 Paare je Körperpaar — ein Viertel der
 * Arbeit. Der Fehler dabei ist klein, weil die Bahnen glatt sind: zwischen zwei
 * Abtastpunkten bewegt sich ein Fuss bei 1 m/s rund 9 mm, und die Prüfung
 * sucht ein Minimum, keinen exakten Wert.
 */
const ASYNC_STEP = 2

/**
 * Tastet die Kapselbahnen über den Zyklus ab — sechs Zahlen je Kapsel und Phase.
 *
 * EINMAL ABTASTEN, MEHRFACH BENUTZEN. Die Paarprüfung braucht diese Bahnen, die
 * Umhüllung braucht dieselben. Zwei Abtastungen wären zwei Wahrheiten über
 * dieselbe Bewegung — und die zweite fiele erst auf, wenn ein Hüllkörper eine
 * Kollision nicht enthält, die die Prüfung meldet.
 *
 * Braucht den Gang und den Bodenkontakt, weil sich die Bauteile im Gang bewegen
 * — nicht im Bild, sondern in der Rechnung. Ohne den Bodenkontakt stünde die
 * Figur auf einer festen Beckenhöhe und die Beine kämen einander anders nahe.
 *
 * Flach abgelegt: danach ist alles Weitere reine Arithmetik ohne einen einzigen
 * Objektzugriff.
 */
export function capsuleTracks(
  skeleton: Skeleton,
  capsules: Capsule[],
  gait: GaitResult,
  ground: GroundResult | undefined,
  samples: number,
): Float64Array[] {
  const n = capsules.length
  const tracks: Float64Array[] = capsules.map(() => new Float64Array(samples * 6))

  for (let i = 0; i < samples; i++) {
    const phase = i / samples
    const index = ground ? Math.min(gait.samples - 1, Math.floor(phase * gait.samples)) : 0
    const pose = poseAt(
      skeleton,
      (joint, plane, side) => sampleAngle(gait, joint, plane, side, phase) ?? 0,
      {
        x: ground ? ground.forward[index] : 0,
        y: skeleton.pelvisRest + (ground ? ground.lift[index] : 0),
        z: ground ? ground.lateral[index] : 0,
      },
    )

    for (let k = 0; k < n; k++) {
      const [a, b] = capsuleEnds(pose, capsules[k])
      const t = tracks[k]
      const o = i * 6
      t[o] = a.x
      t[o + 1] = a.y
      t[o + 2] = a.z
      t[o + 3] = b.x
      t[o + 4] = b.y
      t[o + 5] = b.z
    }
  }

  return tracks
}

/**
 * Prüft alle Paare über den Zyklus.
 */
export function computeClearance(
  model: HumanoidModel,
  skeleton: Skeleton,
  gait: GaitResult,
  ground?: GroundResult,
): ClearanceResult {
  const capsules = buildCapsules(skeleton, model.scale)
  const skip = neighbourPairs()
  const n = capsules.length

  const tracks = capsuleTracks(skeleton, capsules, gait, ground, SAMPLES)

  const pairs: ClearancePair[] = []

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const A = capsules[i]
      const B = capsules[j]
      if (skip.has(`${A.key}|${B.key}`)) continue

      const pa = tracks[i]
      const pb = tracks[j]
      const reach = A.radius + B.radius

      let sync = Infinity
      let syncPhase = 0
      for (let t = 0; t < SAMPLES; t++) {
        const o = t * 6
        const d =
          segmentDistance(
            pa[o],
            pa[o + 1],
            pa[o + 2],
            pa[o + 3],
            pa[o + 4],
            pa[o + 5],
            pb[o],
            pb[o + 1],
            pb[o + 2],
            pb[o + 3],
            pb[o + 4],
            pb[o + 5],
          ) - reach
        if (d < sync) {
          sync = d
          syncPhase = t / SAMPLES
        }
      }

      let async = Infinity
      let phaseA = 0
      let phaseB = 0
      for (let t1 = 0; t1 < SAMPLES; t1 += ASYNC_STEP) {
        const u = t1 * 6
        for (let t2 = 0; t2 < SAMPLES; t2 += ASYNC_STEP) {
          const v = t2 * 6
          const d =
            segmentDistance(
              pa[u],
              pa[u + 1],
              pa[u + 2],
              pa[u + 3],
              pa[u + 4],
              pa[u + 5],
              pb[v],
              pb[v + 1],
              pb[v + 2],
              pb[v + 3],
              pb[v + 4],
              pb[v + 5],
            ) - reach
          if (d < async) {
            async = d
            phaseA = t1 / SAMPLES
            phaseB = t2 / SAMPLES
          }
        }
      }

      /*
        Der gleichzeitige Fall IST ein Sonderfall des zeitversetzten. Die
        gröbere Abtastung des zeitversetzten Scans trifft aber nicht jede
        Phase — läge das gleichzeitige Minimum auf einer ungeraden Phase, käme
        „zeitversetzt weiter auseinander als gleichzeitig" heraus, und das ist
        Unsinn. Deshalb hier zusammengeführt statt die Abtastung zu verdoppeln.
      */
      if (sync < async) {
        async = sync
        phaseA = syncPhase
        phaseB = syncPhase
      }

      pairs.push({
        a: A.key,
        b: B.key,
        labelA: A.label,
        labelB: B.label,
        synchronous: sync,
        synchronousPhase: syncPhase,
        asynchronous: async,
        phaseA,
        phaseB,
        contralateral: A.side !== undefined && B.side !== undefined && A.side !== B.side,
      })
    }
  }

  pairs.sort((x, y) => x.synchronous - y.synchronous)

  return {
    pairs,
    tightest: pairs.length ? pairs[0].synchronous : Infinity,
    breaches: pairs.filter((p) => p.synchronous < CLEARANCE_MIN).length,
    /* Die stillen Fälle: gleichzeitig unauffällig, zeitversetzt zu eng.
       OHNE die gegenüberliegenden Gliedmassen — bei denen ist der zeitversetzte
       Abstand keine Aussage über den Aufbau, sondern über einen Taktverlust. */
    latent: pairs.filter(
      (p) => !p.contralateral && p.synchronous >= CLEARANCE_MIN && p.asynchronous < CLEARANCE_MIN,
    ).length,
    /* Und die Gegenseite eigens gezählt: sie soll sichtbar bleiben, nur nicht
       als Entwurfsfehler durchgehen. */
    outOfStep: pairs.filter(
      (p) => p.contralateral && p.synchronous >= CLEARANCE_MIN && p.asynchronous < CLEARANCE_MIN,
    ).length,
    bodies: n,
    samples: SAMPLES,
  }
}

/** Der kleinste Abstand in EINER Stellung — für die Grundstellung und Grenzlagen. */
export function clearanceAt(
  pose: Pose,
  capsules: Capsule[],
  skip: Set<string> = neighbourPairs(),
): { distance: number; a?: Capsule; b?: Capsule } {
  const ends = capsules.map((c) => capsuleEnds(pose, c))
  let best = Infinity
  let ba: Capsule | undefined
  let bb: Capsule | undefined

  for (let i = 0; i < capsules.length; i++) {
    for (let j = i + 1; j < capsules.length; j++) {
      if (skip.has(`${capsules[i].key}|${capsules[j].key}`)) continue
      const [a1, a2] = ends[i]
      const [b1, b2] = ends[j]
      const d =
        segmentDistance(a1.x, a1.y, a1.z, a2.x, a2.y, a2.z, b1.x, b1.y, b1.z, b2.x, b2.y, b2.z) -
        capsules[i].radius -
        capsules[j].radius
      if (d < best) {
        best = d
        ba = capsules[i]
        bb = capsules[j]
      }
    }
  }

  return { distance: best, a: ba, b: bb }
}
